import { useEffect, useState } from 'react';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import type { PurchasesOfferings } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useAuthStore } from '../store/useAuthStore';

// We will inject the API key here once the user provides it
const REVENUECAT_API_KEY_IOS = import.meta.env.VITE_REVENUECAT_IOS_KEY || '';
const REVENUECAT_API_KEY_ANDROID = import.meta.env.VITE_REVENUECAT_ANDROID_KEY || '';

let isConfigured = false;

export function usePurchases() {
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const { session } = useAuthStore();

  useEffect(() => {
    const initRevenueCat = async () => {
      try {
        if (!Capacitor.isNativePlatform()) {
          console.log('RevenueCat: Not running on a native platform, skipping init');
          setIsInitializing(false);
          return;
        }

        const apiKey = Capacitor.getPlatform() === 'ios' ? REVENUECAT_API_KEY_IOS : REVENUECAT_API_KEY_ANDROID;
        
        if (!apiKey) {
          console.warn('RevenueCat API Key missing');
          setIsInitializing(false);
          return;
        }

        if (!isConfigured) {
          await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

          if (session?.user?.id) {
            await Purchases.configure({ apiKey, appUserID: session.user.id });
          } else {
            await Purchases.configure({ apiKey });
          }
          isConfigured = true;
        } else if (session?.user?.id) {
          // If already configured but user changed, we should log them in instead of reconfiguring
          await Purchases.logIn({ appUserID: session.user.id });
        }

        // Fetch user info to check current pro status
        const { customerInfo } = await Purchases.getCustomerInfo();
        const rcIsPro = typeof customerInfo.entitlements.active['pro'] !== 'undefined';
        if (rcIsPro) {
          useAuthStore.setState({ isPro: true });
        }

        // Fetch offerings for the paywall
        const fetchedOfferings = await Purchases.getOfferings();
        setOfferings(fetchedOfferings);
        
      } catch (e) {
        console.error('Error initializing RevenueCat', e);
      } finally {
        setIsInitializing(false);
      }
    };

    initRevenueCat();
  }, [session?.user?.id]);

  const purchasePackage = async (rcPackage: any) => {
    if (!Capacitor.isNativePlatform()) return false;
    
    setIsPurchasing(true);
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: rcPackage });
      const rcIsPro = typeof customerInfo.entitlements.active['pro'] !== 'undefined';
      if (rcIsPro) {
        useAuthStore.setState({ isPro: true });
      }
      return rcIsPro;
    } catch (e: any) {
      if (!e.userCancelled) {
        console.error('Error purchasing package', e);
      }
      return false;
    } finally {
      setIsPurchasing(false);
    }
  };

  const restorePurchases = async () => {
    if (!Capacitor.isNativePlatform()) return false;

    setIsPurchasing(true);
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const rcIsPro = typeof customerInfo.entitlements.active['pro'] !== 'undefined';
      if (rcIsPro) {
        useAuthStore.setState({ isPro: true });
      }
      return rcIsPro;
    } catch (e) {
      console.error('Error restoring purchases', e);
      return false;
    } finally {
      setIsPurchasing(false);
    }
  };

  const manageSubscriptions = async () => {
    if (!Capacitor.isNativePlatform()) {
      console.log('RevenueCat: Not running on a native platform');
      return;
    }
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      if (customerInfo.managementURL) {
        await Browser.open({ url: customerInfo.managementURL });
      } else {
        console.warn('No management URL found');
      }
    } catch (e) {
      console.error('Error opening subscription management', e);
    }
  };

  return {
    offerings,
    isInitializing,
    isPurchasing,
    purchasePackage,
    restorePurchases,
    manageSubscriptions
  };
}
